/* Per-item notes on the cart page.
   Uses line_item property "Notiz" and writes via /cart/change.js. */

class CartItemNote extends HTMLElement {
  constructor() {
    super();
    this.itemKey = this.getAttribute('data-item-key');
    this.openButtons = this.querySelectorAll('[data-action="open"]');
    this.editor = this.querySelector('[data-editor]');
    this.textarea = this.querySelector('[data-textarea]');
    this.cancelBtn = this.querySelector('[data-action="cancel"]');
    this.saveBtn = this.querySelector('[data-action="save"]');
    this.statusEl = this.querySelector('[data-status]');

    this.openButtons.forEach((btn) => btn.addEventListener('click', () => this.open()));
    if (this.cancelBtn) this.cancelBtn.addEventListener('click', () => this.close());
    if (this.saveBtn) this.saveBtn.addEventListener('click', () => this.save());
  }

  open() {
    if (!this.editor) return;
    this.querySelectorAll('[data-action="open"]').forEach((btn) => {
      btn.hidden = true;
    });
    this.editor.hidden = false;
    if (this.textarea) {
      this.textarea.focus();
      this.textarea.setSelectionRange(this.textarea.value.length, this.textarea.value.length);
    }
  }

  close() {
    if (!this.editor) return;
    this.editor.hidden = true;
    this.querySelectorAll('[data-action="open"]').forEach((btn) => {
      btn.hidden = false;
    });
    if (this.statusEl) this.statusEl.textContent = '';
  }

  setStatus(message, type) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message || '';
    this.statusEl.dataset.status = type || '';
  }

  async save() {
    if (!this.itemKey || !this.textarea) return;
    const value = this.textarea.value.trim();

    this.saveBtn.disabled = true;
    this.setStatus('Wird gespeichert…', 'pending');

    try {
      // 1) Load current line properties so we don't overwrite them
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      const item = cart.items.find((it) => it.key === this.itemKey);
      if (!item) throw new Error('Cart item not found');

      const nextProperties = { ...(item.properties || {}) };
      if (value === '') {
        delete nextProperties['Notiz'];
      } else {
        nextProperties['Notiz'] = value;
      }

      // 2) Update just this line's properties
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: this.itemKey,
          properties: nextProperties
        })
      });

      if (!changeRes.ok) {
        const txt = await changeRes.text();
        throw new Error('Cart change failed: ' + changeRes.status + ' ' + txt);
      }

      this.setStatus(value ? 'Notiz gespeichert' : 'Notiz entfernt', 'success');

      // 3) Refresh the cart section so server-rendered state is in sync
      await this.rerenderCart();
    } catch (err) {
      console.error('[CartItemNote] save failed', err);
      this.setStatus('Fehler beim Speichern. Bitte erneut versuchen.', 'error');
      this.saveBtn.disabled = false;
    }
  }

  async rerenderCart() {
    try {
      const sectionsToFetch = [
        'main-cart-items',
        'main-cart-footer',
        'cart-icon-bubble',
        'mobile-cart-icon-bubble'
      ].filter(Boolean);

      const url = `/?sections=${sectionsToFetch.join(',')}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) {
        // Fallback — just reload the page
        window.location.reload();
        return;
      }
      const data = await res.json();

      // Replace each section's contents in-place
      Object.entries(data).forEach(([sectionKey, html]) => {
        // Shopify returns the full section HTML; extract the first element inside
        const tempDoc = new DOMParser().parseFromString(html, 'text/html');
        const newSection = tempDoc.querySelector(`#shopify-section-${sectionKey}`) || tempDoc.body.firstElementChild;
        if (!newSection) return;

        // Find target section in page by matching data-section
        const allShopifySections = document.querySelectorAll(`[id^="shopify-section-"]`);
        allShopifySections.forEach((sec) => {
          if (sec.id.endsWith(`__${sectionKey}`) || sec.id === `shopify-section-${sectionKey}`) {
            const newInner = newSection.innerHTML;
            sec.innerHTML = newInner;
          }
        });
      });

      // Dispatch generic cart:updated so other scripts pick up state
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.warn('[CartItemNote] re-render failed, reloading', err);
      window.location.reload();
    }
  }
}

if (!customElements.get('cart-item-note')) {
  customElements.define('cart-item-note', CartItemNote);
}
